import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { makePrioStyles } from '../../../theme/utils';
import { ContactId, ProjectId } from '../../../models/Types';
import { Contact } from '../../../models/Contact';
import { Form, Typography, Switch, Row, Col } from 'antd';
import {
  SettingKey,
  CreateSetting,
  UpdateSetting,
} from '../../../models/Settings';
import {
  apiFetchSettings,
  apiSaveProjectSetting,
  apiUpdateProjectSetting,
} from '../../settings/api';
import {
  Button,
  InformationBox,
  Input,
  PrioSpinner,
  Select,
} from '@prio365/prio365-react-library';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ContactPickerPrioCL from '../../contacts/components/ContactPickerPrioCL';
import deepEqual from 'deep-equal';
import { emailPattern } from '../../../hooks/useEmailValidation';
import Flex from '../../../components/Flex';
import useInternalProjectContactsContext from '../hooks/useInternalProjectContactsContext';
import useContactsContext from '../../contacts/hooks/useContactsProvider';
import { fetchInternalProjectContacts } from '../actions';

const useStyles = makePrioStyles((theme) => ({
  root: {
    marginTop: theme.spacing.large,
    padding: theme.spacing.large,
    backgroundColor: theme.colors.application.background.default,
  },
  formItem: {
    marginBottom: theme.spacing.regular,
  },
  button: {
    marginTop: theme.spacing.large,
    float: 'right',
  },
}));

interface MailAssignmentForm {
  enabled: boolean;
  aiAssignment: boolean;
  salutationAssignment: boolean;
  sentHeaderAssignment: boolean;
  contactsAlwaysExcludedFromAutoAssignments: ContactId[];
  skipContactsThatAreAbsent: boolean;
  isDefaultAssigneeEnabled: boolean;
  userId: string | null;
  fallbackUserId: string | null;
  shouldSendMessage: boolean;
  recipientEmailAddress: string | null;
}

const defaultValues: MailAssignmentForm = {
  enabled: false,
  aiAssignment: false,
  salutationAssignment: false,
  sentHeaderAssignment: false,
  contactsAlwaysExcludedFromAutoAssignments: [],
  skipContactsThatAreAbsent: false,
  isDefaultAssigneeEnabled: false,
  userId: '',
  fallbackUserId: '',
  shouldSendMessage: false,
  recipientEmailAddress: '',
};

interface ProjectMailAssignmentSettingProps {
  className?: string;
  projectId: ProjectId;
}

export const ProjectMailAssignmentSetting: React.FC<
  ProjectMailAssignmentSettingProps
> = (props) => {
  //#region ------------------------------ Defaults
  const { className, projectId } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [showPart2, setShowPart2] = useState(false);
  const [showFallbackItems, setShowFallbackItems] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [showMailInput, setShowMailInput] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [autoAssignProjectSettingId, setAutoAssignProjectSettingId] = useState<
    string | null
  >(null);
  const [aiAssignProjectSettingId, setAiAssignProjectSettingId] = useState<
    string | null
  >(null);
  const [defaultAssigneeProjectSettingId, setDefaultAssigneeProjectSettingId] =
    useState<string | null>(null);
  const [defaultMessageProjectSettingId, setDefaultMessageProjectSettingId] =
    useState<string | null>(null);
  const [userId, setUserId] = useState<ContactId | null>(null);
  const [fallbackUserId, setFallbackUserId] = useState<ContactId | null>(null);

  const { getInternalProjectContactsContacts, hasInternalProjectContacts } =
    useInternalProjectContactsContext();

  const { getContactById } = useContactsContext(true);

  const { data: autoAssignData, isLoading: loadingAutoAssign } = useQuery({
    queryKey: [projectId, 'setting:SharedMailbox_AutoAssignmentConfiguration'],
    queryFn: () =>
      apiFetchSettings([
        {
          keys: [projectId, 'SharedMailbox_AutoAssignmentConfiguration'],
          scopeId: projectId,
          eSettingScope: 'project',
        } as SettingKey,
      ]),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const { data: aiAssignData, isLoading: loadingAiAssign } = useQuery({
    queryKey: [projectId, 'setting:Cognitive_AutoAssignMailsWithAi'],
    queryFn: () =>
      apiFetchSettings([
        {
          keys: [projectId, 'Cognitive_AutoAssignMailsWithAi'],
          scopeId: projectId,
          eSettingScope: 'project',
        } as SettingKey,
      ]),
    staleTime: 1000 * 60 * 5,
  });

  const { data: defaultAssigneeData, isLoading: loadingDefaultAssignee } =
    useQuery({
      queryKey: [projectId, 'setting:SharedMailbox_DefaultAssignee'],
      queryFn: () =>
        apiFetchSettings([
          {
            keys: [projectId, 'SharedMailbox_DefaultAssignee'],
            scopeId: projectId,
            eSettingScope: 'project',
          } as SettingKey,
        ]),
      staleTime: 1000 * 60 * 5, // 5 minutes
    });

  const { data: defaultMessageData, isLoading: loadingDefaultMessage } =
    useQuery({
      queryKey: [
        projectId,
        'setting:SharedMailbox_ShouldSendMessageWhenNoUserWasAssigned',
      ],
      queryFn: () =>
        apiFetchSettings([
          {
            keys: [
              projectId,
              'SharedMailbox_ShouldSendMessageWhenNoUserWasAssigned',
            ],
            scopeId: projectId,
            eSettingScope: 'project',
          } as SettingKey,
        ]),
      staleTime: 1000 * 60 * 5, // 5 minutes
    });

  const initialValues: MailAssignmentForm = useMemo(() => {
    const initialAssignDataValues = autoAssignData?.data?.[0]
      ? JSON.parse(autoAssignData.data[0].jsonValue)
      : {
          enabled: false,
          sentHeaderAssignment: false,
          salutationAssignment: false,
          skipContactsThatAreAbsent: false,
          contactsAlwaysExcludedFromAutoAssignments: [],
        };
    const initialAiAssignValue = aiAssignData?.data?.[0]
      ? JSON.parse(aiAssignData.data[0].jsonValue)
      : { isEnabled: false };
    const initialDefaultAssigneeValues = defaultAssigneeData?.data?.[0]
      ? JSON.parse(defaultAssigneeData.data[0].jsonValue)
      : { userId: '', fallbackUserId: '' };
    const initialDefaultMessageValue = defaultMessageData?.data?.[0]
      ? JSON.parse(defaultMessageData.data[0].jsonValue)
      : { shouldSendMessage: false, recipientEmailAddress: '' };
    setShowPart2(initialAssignDataValues.enabled as boolean);
    setShowFallbackItems(
      initialAssignDataValues.skipContactsThatAreAbsent as boolean
    );
    setShowUsers(
      initialDefaultAssigneeValues.userId ||
        initialDefaultAssigneeValues.fallbackUserId
        ? true
        : false
    );
    setShowMailInput(initialDefaultMessageValue.shouldSendMessage as boolean);
    return {
      enabled: initialAssignDataValues.enabled as boolean,
      aiAssignment: (initialAiAssignValue.isEnabled as boolean) ?? false,
      sentHeaderAssignment:
        initialAssignDataValues.sentHeaderAssignment as boolean,
      salutationAssignment:
        initialAssignDataValues.salutationAssignment as boolean,
      skipContactsThatAreAbsent:
        initialAssignDataValues.skipContactsThatAreAbsent as boolean,
      contactsAlwaysExcludedFromAutoAssignments:
        (initialAssignDataValues.contactsAlwaysExcludedFromAutoAssignments as ContactId[]) ||
        [],
      isDefaultAssigneeEnabled: initialDefaultAssigneeValues.userId
        ? true
        : false,
      userId: initialDefaultAssigneeValues.userId || '',
      fallbackUserId: initialDefaultAssigneeValues.fallbackUserId || '',
      shouldSendMessage:
        initialDefaultMessageValue.shouldSendMessage as boolean,
      recipientEmailAddress:
        initialDefaultMessageValue.recipientEmailAddress || null,
    };
  }, [autoAssignData, aiAssignData, defaultAssigneeData, defaultMessageData]);

  const excludedContactOptions = useMemo(() => {
    const membersLoaded = hasInternalProjectContacts(projectId);
    const toOption = (
      contact: Contact | undefined,
      contactId: ContactId,
      isFormerMember: boolean
    ) => {
      const name = contact
        ? `${contact.firstName} ${contact.lastName}`
        : t('projects:projectMailAssignmentSetting.excludedContacts.unknown');
      const marker = contact?.isArchived
        ? t('projects:projectMailAssignmentSetting.excludedContacts.archived')
        : isFormerMember
        ? t(
            'projects:projectMailAssignmentSetting.excludedContacts.formerMember'
          )
        : null;
      return {
        value: contactId,
        label: marker ? `${name} (${marker})` : name,
        searchValue: name,
      };
    };
    const memberContacts = getInternalProjectContactsContacts(projectId);
    const memberIds = new Set(
      memberContacts.map((contact) => contact.contactId)
    );
    return [
      ...memberContacts.map((contact) =>
        toOption(contact, contact.contactId, false)
      ),
      ...initialValues.contactsAlwaysExcludedFromAutoAssignments
        .filter((contactId) => !memberIds.has(contactId))
        .map((contactId) =>
          toOption(getContactById(contactId), contactId, membersLoaded)
        ),
    ];
  }, [
    getInternalProjectContactsContacts,
    hasInternalProjectContacts,
    projectId,
    getContactById,
    initialValues.contactsAlwaysExcludedFromAutoAssignments,
    t,
  ]);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const checkSettingExists = (settingId: string | null) => {
    return (
      settingId !== '00000000-0000-0000-0000-000000000000' && settingId !== null
    );
  };

  const generateCreateSetting = (
    jsonValue: Record<string, any>
  ): CreateSetting => {
    return {
      jsonValue: JSON.stringify(jsonValue),
      parentSettingId: '',
      relationId: '',
      isDefault: true,
      description: '',
    };
  };

  const saveSetting = async (
    settingKey: string,
    settingId: string,
    setting: CreateSetting
  ) => {
    try {
      if (checkSettingExists(settingId)) {
        await apiUpdateProjectSetting(settingId, {
          ...setting,
          scope: 'project',
        } as UpdateSetting);
      } else {
        await apiSaveProjectSetting(
          projectId,
          settingKey,
          setting as CreateSetting
        );
      }

      queryClient.invalidateQueries({
        queryKey: [projectId, `setting:${settingKey}`],
      });
    } catch (error) {}
  };

  const handleSave = async (currentValues: MailAssignmentForm) => {
    const hasFormItemsChanged = (formItems: string[]): boolean => {
      // for correct comparison. some form.items depend on other form.items values and can (dis)appear from _current
      const current = { ...defaultValues, ...currentValues };

      const changedFormItems = Object.keys(current).filter(
        (key) => !deepEqual(initialValues[key], current[key])
      );

      return changedFormItems.some((item) => formItems.includes(item));
    };

    try {
      const settingDataAutoAssign = generateCreateSetting({
        enabled: form.getFieldValue('enabled'),
        sentHeaderAssignment: form.getFieldValue('sentHeaderAssignment'),
        salutationAssignment: form.getFieldValue('salutationAssignment'),
        skipContactsThatAreAbsent: form.getFieldValue(
          'skipContactsThatAreAbsent'
        ),
        contactsAlwaysExcludedFromAutoAssignments:
          form.getFieldValue('contactsAlwaysExcludedFromAutoAssignments') || [],
      });

      const settingDataAiAssign = generateCreateSetting({
        isEnabled: form.getFieldValue('aiAssignment'),
      });

      const settingDataDefaultAssignee = generateCreateSetting({
        userId: form.getFieldValue('userId'),
        fallbackUserId: form.getFieldValue('fallbackUserId'),
      });

      const settingDataDefaultMessage = generateCreateSetting({
        shouldSendMessage: form.getFieldValue('shouldSendMessage')
          ? true
          : false,
        recipientEmailAddress: form.getFieldValue('shouldSendMessage')
          ? form.getFieldValue('recipientEmailAddress') || ''
          : undefined,
      });

      if (
        hasFormItemsChanged([
          'enabled',
          'sentHeaderAssignment',
          'salutationAssignment',
          'skipContactsThatAreAbsent',
          'contactsAlwaysExcludedFromAutoAssignments',
        ])
      ) {
        await saveSetting(
          'SharedMailbox_AutoAssignmentConfiguration',
          autoAssignProjectSettingId,
          settingDataAutoAssign
        );
      }

      if (hasFormItemsChanged(['aiAssignment'])) {
        await saveSetting(
          'Cognitive_AutoAssignMailsWithAi',
          aiAssignProjectSettingId,
          settingDataAiAssign
        );
      }

      if (hasFormItemsChanged(['userId', 'fallbackUserId'])) {
        await saveSetting(
          'SharedMailbox_DefaultAssignee',
          defaultAssigneeProjectSettingId,
          settingDataDefaultAssignee
        );
      }

      if (hasFormItemsChanged(['shouldSendMessage', 'recipientEmailAddress'])) {
        await saveSetting(
          'SharedMailbox_ShouldSendMessageWhenNoUserWasAssigned',
          defaultMessageProjectSettingId,
          settingDataDefaultMessage
        );
      }

      setIsSaveDisabled(true);
    } catch (error) {}
  };

  const onEnableAutoAssignment = (value: boolean) => {
    setShowPart2(value);
    if (value) {
      form.setFieldsValue({
        salutationAssignment: true,
      });
    } else {
      form.setFieldsValue({
        skipContactsThatAreAbsent: false,
        isDefaultAssigneeEnabled: false,
        shouldSendMessage: false,
        sentHeaderAssignment: false,
        salutationAssignment: false,
        aiAssignment: false,
        contactsAlwaysExcludedFromAutoAssignments: [],
        recipientEmailAddress: null,
        userId: null,
        fallbackUserId: null,
      });
    }
  };

  const onEnableSkipOnAbsence = (value: boolean) => {
    setShowFallbackItems(value);
    if (!value) {
      form.setFieldsValue({
        fallbackUserId: '',
        shouldSendMessage: false,
        recipientEmailAddress: '',
      });
    }
  };

  const onEnableDefaultAssignees = (checked) => {
    setShowUsers(checked);
    if (!checked) {
      form.setFieldsValue({
        userId: '',
        fallbackUserId: '',
        shouldSendMessage: false,
        recipientEmailAddress: '',
      });
      setShowMailInput(false);
    }
  };

  const onEnableShouldSendMessage = (checked) => {
    setShowMailInput(checked);
    if (!checked) {
      form.setFieldsValue({
        recipientEmailAddress: null,
      });
    }
  };

  const handleDisableButton = () => {
    const _current = form.getFieldsValue();
    // for correct comparison. some form.items depend on other form.items values and can (dis)appear from _current
    const current = { ...defaultValues, ..._current };

    const isDirty = !deepEqual(current, initialValues);
    const hasErrors = form.getFieldsError().some((f) => f.errors.length > 0);

    setIsSaveDisabled(!isDirty || hasErrors);
  };

  const handleValuesChange = (changedValues: MailAssignmentForm) => {
    if (changedValues.shouldSendMessage !== undefined) {
      onEnableShouldSendMessage(changedValues.shouldSendMessage);
    }

    if (changedValues.isDefaultAssigneeEnabled !== undefined) {
      onEnableDefaultAssignees(changedValues.isDefaultAssigneeEnabled);
    }

    if (changedValues.skipContactsThatAreAbsent !== undefined) {
      onEnableSkipOnAbsence(changedValues.skipContactsThatAreAbsent);
    }

    if (changedValues.enabled !== undefined) {
      onEnableAutoAssignment(changedValues.enabled);
    }

    if (changedValues.userId) {
      setUserId(changedValues.userId);
    }
    if (changedValues.fallbackUserId) {
      setFallbackUserId(changedValues.fallbackUserId);
    }
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    dispatch(fetchInternalProjectContacts(projectId));
  }, [dispatch, projectId]);

  useEffect(() => {
    // Don't run until all queries have settled:
    if (
      loadingAutoAssign ||
      loadingAiAssign ||
      loadingDefaultAssignee ||
      loadingDefaultMessage
    ) {
      return;
    }

    form.setFieldsValue(initialValues);
    setUserId(initialValues.userId ? initialValues.userId : null);
    setFallbackUserId(
      initialValues.fallbackUserId ? initialValues.fallbackUserId : null
    );

    const globalCheck = (settingData) =>
      settingData?.data?.[0]?.scope === 'global';

    setAutoAssignProjectSettingId(
      globalCheck ? null : autoAssignData?.data?.[0]?.settingId || null
    );
    setAiAssignProjectSettingId(
      globalCheck ? null : aiAssignData?.data?.[0]?.settingId || null
    );
    setDefaultAssigneeProjectSettingId(
      globalCheck ? null : defaultAssigneeData?.data?.[0]?.settingId || null
    );
    setDefaultMessageProjectSettingId(
      globalCheck ? null : defaultMessageData?.data?.[0]?.settingId || null
    );
  }, [
    loadingAutoAssign,
    loadingAiAssign,
    loadingDefaultAssignee,
    loadingDefaultMessage,
    initialValues,
    autoAssignData,
    aiAssignData,
    defaultAssigneeData,
    defaultMessageData,
    form,
  ]);
  //#endregion

  if (
    loadingDefaultMessage ||
    loadingDefaultAssignee ||
    loadingAutoAssign ||
    loadingAiAssign
  ) {
    return (
      <div className={classNames(classes.root, className)}>
        <Typography.Title>
          {t(`projects:projectMailAssignmentSetting.headline`)}
        </Typography.Title>
        <Flex.Row justifyContent="center" width="100%">
          <PrioSpinner />
        </Flex.Row>
      </div>
    );
  }

  return (
    <div className={classNames(classes.root, className)}>
      <Typography.Title>
        {t(`projects:projectMailAssignmentSetting.headline`)}
      </Typography.Title>

      <Form<MailAssignmentForm>
        layout="vertical"
        form={form}
        initialValues={initialValues}
        onValuesChange={handleValuesChange}
        onFieldsChange={handleDisableButton}
        onFinish={handleSave}
      >
        {/* ===== Level 1: Master Toggle ===== */}
        <InformationBox
          description={t(
            `projects:projectMailAssignmentSetting.descriptionAutoAssign`
          )}
          marginBottom="small"
        />
        <Row gutter={16}>
          <Col span={4}>
            <Form.Item
              name={'enabled'}
              label={t(
                'projects:projectMailAssignmentSetting.form.labels.autoAssignmentEnable'
              )}
              className={classes.formItem}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        {showPart2 && (
          <>
            {/* ===== Level 2: AI Assignment ===== */}
            <InformationBox
              description={t(
                `projects:projectMailAssignmentSetting.descriptionAiAssignment`
              )}
              marginBottom="small"
              marginTop="regular"
            />
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={'aiAssignment'}
                  label={t(
                    'projects:projectMailAssignmentSetting.form.labels.aiAssignment'
                  )}
                  className={classes.formItem}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            {/* ===== Level 2: Sent Header Assignment ===== */}
            <InformationBox
              description={t(
                `projects:projectMailAssignmentSetting.descriptionSentHeaderAssignment`
              )}
              marginBottom="small"
              marginTop="regular"
            />
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={'sentHeaderAssignment'}
                  label={t(
                    'projects:projectMailAssignmentSetting.form.labels.SentHeaderAssignment'
                  )}
                  className={classes.formItem}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            {/* ===== Level 2: Excluded Contacts ===== */}
            <InformationBox
              description={t(
                `projects:projectMailAssignmentSetting.descriptionExcludedContacts`
              )}
              marginBottom="small"
              marginTop="regular"
            />
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={'contactsAlwaysExcludedFromAutoAssignments'}
                  label={t(
                    'projects:projectMailAssignmentSetting.form.labels.excludedContacts'
                  )}
                  className={classes.formItem}
                >
                  <Select
                    mode="multiple"
                    showSearch
                    size="default"
                    options={excludedContactOptions}
                    filterOption={(input, option) =>
                      option.searchValue
                        ?.toString()
                        ?.toLowerCase()
                        ?.indexOf(input.toLowerCase()) >= 0
                    }
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* ===== Level 2: Absence ===== */}
            <InformationBox
              description={t(
                `projects:projectMailAssignmentSetting.descriptionAbsence`
              )}
              marginBottom="small"
              marginTop="regular"
            />
            <Row gutter={16}>
              <Col span={4}>
                <Form.Item
                  name={'skipContactsThatAreAbsent'}
                  label={t(
                    'projects:projectMailAssignmentSetting.form.labels.enable'
                  )}
                  className={classes.formItem}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
            <InformationBox
              description={t(
                `projects:projectMailAssignmentSetting.${!form.getFieldValue('skipContactsThatAreAbsent') ? 'descriptionDefaultAssignee' : 'descriptionDefaultAssigneeWithFallback'}`
              )}
              marginBottom="small"
              marginTop="regular"
            />
            <Row gutter={16}>
              <Col span={4}>
                <Form.Item
                  name={'isDefaultAssigneeEnabled'}
                  label={t(
                    'projects:projectMailAssignmentSetting.form.labels.enable'
                  )}
                  className={classes.formItem}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              {showUsers && (
                <>
                  <Col span={10}>
                    <Form.Item
                      name="userId"
                      label={t(
                        'projects:projectMailAssignmentSetting.form.labels.UserId'
                      )}
                      rules={[
                        {
                          required: showUsers,
                          message: t(
                            'projects:projectMailAssignmentSetting.form.error.missingUserId'
                          ),
                        },
                      ]}
                    >
                      <ContactPickerPrioCL
                        onlyInternalProject
                        projectId={projectId}
                        size="default"
                        excludedContactIds={
                          fallbackUserId ? [fallbackUserId] : []
                        }
                        hideShowMoreButton
                      />
                    </Form.Item>
                  </Col>
                  {showFallbackItems && (
                    <Col span={10}>
                      <Form.Item
                        name="fallbackUserId"
                        label={t(
                          'projects:projectMailAssignmentSetting.form.labels.FallbackUserId'
                        )}
                        rules={[
                          {
                            required: showUsers,
                            message: t(
                              'projects:projectMailAssignmentSetting.form.error.missingUserId'
                            ),
                          },
                        ]}
                      >
                        <ContactPickerPrioCL
                          onlyInternalProject
                          projectId={projectId}
                          size="default"
                          excludedContactIds={userId ? [userId] : []}
                          hideShowMoreButton
                        />
                      </Form.Item>
                    </Col>
                  )}
                </>
              )}
            </Row>
            {showFallbackItems && showUsers && (
              <>
                <InformationBox
                  description={t(
                    `projects:projectMailAssignmentSetting.descriptionDefaultMessage`
                  )}
                  marginBottom="small"
                  marginTop="regular"
                />
                <Row gutter={16}>
                  <Col span={4}>
                    <Form.Item
                      name={'shouldSendMessage'}
                      label={t(
                        'projects:projectMailAssignmentSetting.form.labels.enable'
                      )}
                      className={classes.formItem}
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  {showMailInput && (
                    <Col span={10}>
                      <Form.Item
                        name={'recipientEmailAddress'}
                        label={t(
                          'projects:projectMailAssignmentSetting.form.labels.RecipientEmailAddress'
                        )}
                        style={{ marginBottom: 0 }}
                        validateTrigger={['onBlur']}
                        rules={[
                          {
                            required: form.getFieldValue('shouldSendMessage'),
                            message: t(
                              'projects:projectMailAssignmentSetting.form.error.missingRecipientEmailAddress'
                            ),
                          },
                          {
                            pattern: emailPattern,
                            message: t(
                              'projects:projectMailAssignmentSetting.form.error.invalidEmail'
                            ),
                          },
                        ]}
                      >
                        <Input full></Input>
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </>
            )}
          </>
        )}
        <Button
          type="primary"
          htmlType={'submit'}
          className={classes.button}
          disabled={isSaveDisabled}
        >
          {t('common:save')}
        </Button>
      </Form>
    </div>
  );
};

export default ProjectMailAssignmentSetting;
